import { Checkbox, CircularProgress, FormControlLabel } from "@mui/material";
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import CommonGrid from "../CommonGrid";
import { api } from "../../../redux/api";
import { useDispatch, useSelector } from "react-redux";
import { newTemplateActions } from "../../../redux/slices/newTemplateSlice";
import ErrorModal from "../../ItemProperties/Forms/FieldDataValidation/ErrorModal";
import { preventTabAndEnter } from "../../../utils/utilFunctions";

const customizedEditorStyling = {
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  gap: ".1rem",
  alignItems: "center",
  resize: "vertical",
  overflow: "hidden",
  zIndex: "100",
};

function TradingPartnerKeyValueGrid({ selectedTP, setEnableApply, setData, setUnsavedChanges, setTPDetails}) {
  const { t } = useTranslation();
  const { account } = useSelector(state => state.queryParams);
  const userPermissions = useSelector((state) => state.userPermissions.permissions);
  const dispatch = useDispatch()
  const [rowData, setRowData] = useState([]);
  const [loadingTPData, setLoadingTPData] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('')
  const CustomCellEditor = forwardRef(({ value, onValueChange }, ref) => {
    const inputRef = useRef();
    const divRef = useRef();

    const resizeHandler = () => {
      const parentHeight = divRef.current.getBoundingClientRect().height;
      inputRef.current.style.height = `${parentHeight} !important`;
      inputRef.current.scrollTo({ bottom: 0 });
      inputRef.current.focus();
    };

    useEffect(() => {
      const resizeObserver = new ResizeObserver(resizeHandler);
      if (divRef.current) {
        resizeObserver.observe(divRef.current);
      }

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    return (
      <div ref={divRef} style={customizedEditorStyling}>
        <textarea
          style={{ height: "100%", outline: "none", width: "100%" }}
          ref={inputRef}
          className="cell-editor-custom-input-accountData"
          type="text"
          value={value}
          autoFocus={true}
          onKeyDown={preventTabAndEnter}
          onChange={({ target: { value } }) => onValueChange(value === "" ? null : value)}
          spellCheck={false}
        />
      </div>
    );
  });

  const gridOptions = {
    defaultColDef: {
      resizable: true,
    },
    columnDefs: [
      {
        headerName: t("LBL_SEARCHABLE"),
        field: "searchable",
        editable: false,
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: (params) => {
          return(
            <FormControlLabel
              sx={{ height: "30px", display: 'flex', alignItems: 'center' }}
              className={"width-fit-content"}
              control={
                <Checkbox
                  name={params.data.key}
                  size="small"
                  checked={params.data.searchable === 'Y' || params.data.searchable === true}
                  disabled={true}
                  style={{ paddingLeft: '10px' }}
                />
              }
            />
          )},
      },
      {
        headerName: t("LBL_UNIQUE"),
        field: "unique",
        editable: false,
        minWidth: 100,
        maxWidth: 100,
        cellRenderer: (params) => {
          return(
            <FormControlLabel
              sx={{ height: "30px", display: 'flex', alignItems: 'center' }}
              className={"width-fit-content"}
              control={
                <Checkbox
                  name={params.data.key}
                  size="small"
                  checked={params.data.unique === 'Y' || params.data.unique === true}
                  disabled={true}
                  style={{ paddingLeft: '10px' }}
                />
              }
            />
          )},
      },
      {
        headerName: t("LBL_KEY"),
        field: "key",
        editable: false,
        flex: 2,
        cellRenderer: (params) => {
          return(
            <div title={params.value} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
              {params.value}
          </div>
      )},
      },
      {
        headerName: t("LBL_FIELD_VALUE"),
        field: "value",
        editable: userPermissions.includes('TRADING_PARTNER_RIGHTS') || userPermissions.includes('TRADING_PARTNER_RIGHTS_MODIFY_VALUE_ONLY'),
        flex: 3,
        cellEditor: CustomCellEditor,
        suppressKeyboardEvent: () => true,
        cellRenderer: (params) => {
          return(
          <div title={params.value} style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
              {params.value}
          </div>
      )},
      },
    ],
  };

  const getGridAPI = (api) => {
    setGridApi(api);
  };

  const handleCellValueChanged = (gridOptions) => {
    const { column, data, oldValue } = gridOptions;
    const colId = column.colId;
    const newValue = data[colId];

    if (newValue === oldValue) return;
    const transformData = (data) => {
      const result = data.reduce((acc, { tradingPartner, key, value }) => {
        if (!acc[tradingPartner]) {
          acc[tradingPartner] = {};
        }
        acc[tradingPartner][key] = value;
        return acc;
      }, {});
      return result
    }

    if (colId === 'value') {
      if (newValue && newValue?.length > 400) {
        const truncatedValue = newValue.slice(0, 400);
        // Update the value in the grid row data
        data[colId] = truncatedValue;
        setErrorModal(true);
        setErrorMessage("Allowed characters are 400.Excess characters have been truncated.");
        gridApi.applyTransaction({ update: [data] });
      }
      setEnableApply(true);
      setUnsavedChanges(true);
      const rowData = [];
      
      gridApi?.forEachNode((node) => {
        rowData.push({ ...node.data, tradingPartner: selectedTP.tradingPartner });
      });
    
      const rData = transformData(rowData)
      setData(rData);
      setTPDetails(rData)
    }
    
  }
  const getTPKeyValuePairs = async (tp) => {
    if(tp){
      setLoadingTPData(true)
      try{
        const response = await dispatch(api.endpoints.getTradingPartnerData.initiate({
          account,
          tp_name:encodeURIComponent(tp)
        },{ forceRefetch: true}))

        const tpDetailsResponse = await dispatch(
          api.endpoints.getTradingPartnerKeysDetails.initiate({
            account,
            tp_name: encodeURIComponent(selectedTP?.tradingPartner)
          }, { forceRefetch: true })
        );

        if(response?.data?.current_state === 'SUCCESS' && tpDetailsResponse?.data?.current_state === 'SUCCESS'){

          const keyValuePairs = response?.data?.data?.trading_partners;
          const filteredPairs = Object.entries(keyValuePairs).filter(item => {
            const [key, value] = item;
            return key.trim() !== ""; 
          });
          const rData = filteredPairs.map(([key, value], index) => ({
            id: index,
            key: key,
            value: value,
            unique: tpDetailsResponse?.data?.data?.trading_partner_details[key]?.unique || "N",
            searchable: tpDetailsResponse?.data?.data?.trading_partner_details[key]?.searchable || "N"
         }));
         rData.sort((a, b) => a.key.localeCompare(b.key));
         setRowData(rData)

        } else {
           setRowData([]);
           dispatch(newTemplateActions.setShowAlert(true));
           dispatch(newTemplateActions.setAlertType('error'));
           dispatch(newTemplateActions.setAlertMessage(response?.data?.message || t('BOX_MSG_GENERAL_ERROR')))
        }
      } catch(error){
        
      }
      finally {
        setLoadingTPData(false)
      }
    } else{
      setRowData([])
    }
  }
  
  useEffect(() => {
    if (selectedTP) {
      getTPKeyValuePairs(selectedTP.tradingPartner);
      setUnsavedChanges(false);
      setData("");
      setTPDetails("");
    }
  }, [selectedTP]);

  return (
    <div style={{ height: "100%" , padding:'0px' }} className="pdc-right-inner">
      { loadingTPData ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}>
          <CircularProgress />{" "}
        </div>
      ) : (
        <CommonGrid
          rowData={rowData}
          gridOptions={gridOptions}
          getGridAPI={getGridAPI}
          containerStyle={{
            height: "100%",
          }}
          gridProps={{
            onCellValueChanged: handleCellValueChanged,
            disableNewRowOnCellValueChange:true
          }}
          stopEditingWhenGridLosesFocus={true}
          animateRows={true}
        />
      )}
      {errorModal && (
        <ErrorModal
          errorModal={errorModal}
          errorMessage={errorMessage}
          OkHandler={() => {
            setErrorModal(false);
          }}
        />
      )}
    </div>
    
  );
}

export default TradingPartnerKeyValueGrid;
